import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useRMSelector } from 'redux/hooks';
import ScoresDropDown from 'components/customComponents/ScoresDropDown';
import AllHitsIcon from '../../../../Icons/Exp_Sidebar/AllHitsIcon.png';
import AnyHitIcon from '../../../../Icons/Exp_Sidebar/AnyHitIcon.png';
import { ReactComponent as MarkAsInteresting } from '../../../../Icons/Exp_Sidebar/MarkAsInteresting.svg';
import { ReactComponent as OnIcon } from 'Icons/Header/on.svg';
import { ReactComponent as OffIcon } from 'Icons/Header/off.svg';
import { Button } from 'react-bootstrap';
import {
  selectAutoSelectNextWell,
  selectHideAutoScores,
  selectHitButtonName,
} from 'redux/selectors/StateSelectors';
import {
  setAutoSelectNextWell,
  setHideAutoScores,
  setHitScoreGroup,
  setHitScoreGroupType,
  setUvHitsOnly,
} from 'redux/reducers/headerReducer';
import ExperimentSidebarButtons from 'components/sidebar/ExperimentSidebarButtons';
import { GetScoreDefn } from 'utils/Global';
import { setExpHitWell, setHitButtonName } from 'redux/reducers/experimentReducer';
import './ScoringTools.scss';
import 'bootstrap/dist/css/bootstrap.css';
import useRMLite from 'CustomHooks/useRMLite';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import { DropDetails } from 'components/well/constants';
import {
  ScoresOfWellDrops,
  WellNavigationDetails,
} from 'components/Experiment/constants';
import { useSelectedDropImageParameters } from 'redux/hooks/experiment';
import ScoreGroupsService from 'business/services/PlateService';
import { HitsButtons } from '../../../sidePanel/searchAndFilter/score/HitsButtons';
import { useHitScoreGroup, useHitScoreGroupType, useUvHitsOnly } from 'redux/hooks/header';
import { CustomSelect } from 'components/input/CustomSelect';
import { FmlxCheckBox } from 'fmlx-common-ui';
import { ScoreConstants } from './constants';

const isUvPositive = (batchScore: { scoreName?: string }): boolean =>
  batchScore.scoreName === ScoreConstants.uvPositiveScore;

interface Props {
  scoresOptionsList: any[];
  selectedWellsScores: ScoresOfWellDrops[][];
  expData?: ExperimentDtoClient;
  wellDetails: WellNavigationDetails | DropDetails;
  applyScoring: (
    wellDropScoreId: number,
    wellDropScoreGroupId: number,
    forceRemoveInteresting?: boolean,
  ) => void;
  imageBatchId?: number;
  dropNumber?: number;
}
const plateService = new ScoreGroupsService();

function ScoringTools(props: Props) {
  const {
    scoresOptionsList,
    selectedWellsScores,
    expData,
    wellDetails,
    applyScoring,
    imageBatchId,
    dropNumber,
  } = props;
  const dispatch = useDispatch();
  const autoSelectNextWell = useRMSelector(selectAutoSelectNextWell);
  const [markIntresting, setMarkIntresting] = useState(false);
  const hideAutoScores = useRMSelector(selectHideAutoScores);
  const wellNumber = wellDetails?.wellNum ?? null;
  const _imageBatchId =
    wellDetails && 'imageBatchId' in wellDetails
      ? wellDetails?.imageBatchId
      : imageBatchId;
  const _dropNumber =
    wellDetails && 'dropNumber' in wellDetails ? wellDetails?.dropNumber : dropNumber;
  const _plates = useRMSelector((state) => {
    return state.experiment?.expData?.plateDto;
  });
  const scoreGroupType = useHitScoreGroupType();
  const scoreGroup = useHitScoreGroup();
  const uvHitsOnly = useUvHitsOnly();
  const [scoreGroupOptions, setScoreGroupOptions] = useState([]);
  const [scoreGroupFilteredOptionsList, setScoreGroupFilteredOptionsList] = useState([]);
  const INTRESTINGSCOREID = 11;
  const hitButtonName = useRMSelector((state) => selectHitButtonName(state));
  const rmLite = useRMLite();
  const isDropMode =
    window.location.pathname.includes('/drop') ||
    window.location.pathname.includes('/compare');
  const isSnfRoute = window.location.pathname.includes('snf');
  const selectedDropImageParameters = useSelectedDropImageParameters();

  const isHitScore = (batchScore: any, scoreDefn: any, manual = false) => {
    if (isUvPositive(batchScore)) {
      return true;
    }
    if (!scoreDefn) {
      return false;
    }
    return manual
      ? scoreDefn.hit === true
      : scoreDefn.hit === true || scoreDefn.scoreName === ScoreConstants.positiveHit;
  };

  const isFilterHit = (batchScore: any, scoreDefn: any, manual = false) => {
    if (uvHitsOnly) {
      return isUvPositive(batchScore);
    }
    return isHitScore(batchScore, scoreDefn, manual);
  };

  useEffect(() => {
    async function fetchScores() {
      const response = await plateService.getScoreForScoringTool();
      setScoreGroupFilteredOptionsList(response);
    }
    fetchScores();
  }, []);

  useEffect(() => {
    if (!selectedWellsScores?.length) {
      return;
    }
    setMarkIntresting(
      selectedWellsScores.some((scores) =>
        scores.some(
          (score) =>
            score.wellDropScoreGroupId === INTRESTINGSCOREID &&
            score.dropNumber === _dropNumber &&
            score.scoreBatchId === _imageBatchId,
        ),
      ),
    );
  }, [selectedWellsScores, wellNumber, _dropNumber, _imageBatchId]);

  function markInterestingWellDrop(key, isActiveScore, forceRemoveInteresting) {
    if (!_imageBatchId) {
      return;
    }
    const score = GetScoreDefn().find((s) => s.hotKey === key);
    applyScoring(
      score.wellDropScoreId,
      score.wellDropScoreGroupId,
      forceRemoveInteresting,
    );
  }

  function toggleAutoSelectNextWell() {
    dispatch(setAutoSelectNextWell(!autoSelectNextWell));
  }

  function toggleHideAutoScores() {
    dispatch(setHideAutoScores(!hideAutoScores));
  }

  function handleScoreGroupType(value) {
    dispatch(setHitScoreGroupType(value.name));
    dispatch(setHitScoreGroup(0));
  }

  function handleScoreGroup(value) {
    dispatch(setHitScoreGroup(value.value));
  }

  useEffect(() => {
    if (scoreGroupType == 'All Scores') {
      setScoreGroupOptions([]);
      const newScoreGroupOptions = [];
      newScoreGroupOptions.push({
        value: 0,
        name: 'All Groups',
      });
      scoreGroupFilteredOptionsList.forEach((sOption) => {
        newScoreGroupOptions.push({
          value: sOption.scoreGroupsId,
          name: sOption.name,
        });
      });

      setScoreGroupOptions(newScoreGroupOptions);
    }
  }, [scoreGroupFilteredOptionsList]);

  useEffect(() => {
    if (scoreGroupType == 'All Scores') {
      setScoreGroupOptions([]);
      const newScoreGroupOptions = [];
      newScoreGroupOptions.push({
        value: 0,
        name: 'All Groups',
      });
      scoreGroupFilteredOptionsList.forEach((sOption) => {
        newScoreGroupOptions.push({
          value: sOption.scoreGroupsId,
          name: sOption.name,
        });
      });

      setScoreGroupOptions(newScoreGroupOptions);
    } else if (scoreGroupType == 'Auto Scores') {
      setScoreGroupOptions([]);
      const newScoreGroupOptions = [];
      newScoreGroupOptions.push({
        value: 0,
        name: 'All Groups',
      });
      scoreGroupFilteredOptionsList.forEach((sOption) => {
        if (sOption.isAutoScore) {
          newScoreGroupOptions.push({
            value: sOption.scoreGroupsId,
            name: sOption.name,
          });
        }
      });

      setScoreGroupOptions(newScoreGroupOptions);
    } else if (scoreGroupType == 'Manual Scores') {
      setScoreGroupOptions([]);
      const newScoreGroupOptions = [];
      newScoreGroupOptions.push({
        value: 0,
        name: 'All Groups',
      });
      scoreGroupFilteredOptionsList.forEach((sOption) => {
        if (sOption.isAutoScore === false) {
          newScoreGroupOptions.push({
            value: sOption.scoreGroupsId,
            name: sOption.name,
          });
        }
      });

      setScoreGroupOptions(newScoreGroupOptions);
    }

    refreshHitFilter();
  }, [scoreGroupType, scoreGroupFilteredOptionsList]);

  useEffect(() => {
    refreshHitFilter();
  }, [_plates, _dropNumber, scoreGroup, uvHitsOnly]);

  const handleAnyHit = async () => {
    const anyHitWells: any[] = [];
    const selectedInspection = expData.inspections.find(
      (insp) => insp.id === selectedDropImageParameters.inspection.id,
    );
    const dropNumber = _dropNumber;

    for (const plate of _plates) {
      const plateScores = plate.plateScores;

      const plateGroupedInsp = selectedInspection.groupedInspections.find(
        (grp) => grp.plateId === plate.plateId,
      );

      plateScores?.forEach((pltScore) => {
        pltScore.forEach((batchScore) => {
          if (batchScore.dropNumber == dropNumber) {
            const scoreDefn = GetScoreDefn().find(
              (s) => s.wellDropScoreId === batchScore.wellDropScoreId,
            );
            if (!scoreDefn && !isUvPositive(batchScore)) {
              return;
            }
            if (scoreGroupType === 'All Scores') {
              if (scoreGroup == 0) {
                if (
                  batchScore.scoreBatchId === plateGroupedInsp.imageBatchId &&
                  isFilterHit(batchScore, scoreDefn)
                ) {
                  if (
                    !anyHitWells.some(
                      (well) =>
                        well.wellNumber === batchScore.wellNumber &&
                        well.plateId === plate.plateId,
                    )
                  ) {
                    const wellObject = {} as any;
                    wellObject.wellNumber = batchScore.wellNumber;
                    wellObject.plateId = plate.plateId;
                    wellObject.hit = true;
                    anyHitWells.push(wellObject);
                  }
                }
              } else {
                if (
                  batchScore.scoreBatchId === plateGroupedInsp.imageBatchId &&
                  isFilterHit(batchScore, scoreDefn) &&
                  batchScore.wellDropScoreGroupId == scoreGroup
                ) {
                  if (
                    !anyHitWells.some(
                      (well) =>
                        well.wellNumber === batchScore.wellNumber &&
                        well.plateId === plate.plateId,
                    )
                  ) {
                    const wellObject = {} as any;
                    wellObject.wellNumber = batchScore.wellNumber;
                    wellObject.plateId = plate.plateId;
                    wellObject.hit = true;
                    anyHitWells.push(wellObject);
                  }
                }
              }
            } else if (scoreGroupType === 'Auto Scores') {
              if (scoreGroup == 0) {
                if (
                  batchScore.scoreBatchId === plateGroupedInsp.imageBatchId &&
                  isFilterHit(batchScore, scoreDefn) &&
                  batchScore.isAutoScore === true
                ) {
                  if (
                    !anyHitWells.some(
                      (well) =>
                        well.wellNumber === batchScore.wellNumber &&
                        well.plateId === plate.plateId,
                    )
                  ) {
                    const wellObject = {} as any;
                    wellObject.wellNumber = batchScore.wellNumber;
                    wellObject.plateId = plate.plateId;
                    wellObject.hit = true;
                    anyHitWells.push(wellObject);
                  }
                }
              } else {
                if (
                  batchScore.scoreBatchId === plateGroupedInsp.imageBatchId &&
                  isFilterHit(batchScore, scoreDefn) &&
                  batchScore.wellDropScoreGroupId == scoreGroup &&
                  batchScore.isAutoScore === true
                ) {
                  if (
                    !anyHitWells.some(
                      (well) =>
                        well.wellNumber === batchScore.wellNumber &&
                        well.plateId === plate.plateId,
                    )
                  ) {
                    const wellObject = {} as any;
                    wellObject.wellNumber = batchScore.wellNumber;
                    wellObject.plateId = plate.plateId;
                    wellObject.hit = true;
                    anyHitWells.push(wellObject);
                  }
                }
              }
            } else if (scoreGroupType === 'Manual Scores') {
              if (scoreGroup == 0) {
                if (
                  batchScore.scoreBatchId === plateGroupedInsp.imageBatchId &&
                  isFilterHit(batchScore, scoreDefn, true) &&
                  batchScore.isAutoScore === false
                ) {
                  if (
                    !anyHitWells.some(
                      (well) =>
                        well.wellNumber === batchScore.wellNumber &&
                        well.plateId === plate.plateId,
                    )
                  ) {
                    const wellObject = {} as any;
                    wellObject.wellNumber = batchScore.wellNumber;
                    wellObject.plateId = plate.plateId;
                    wellObject.hit = true;
                    anyHitWells.push(wellObject);
                  }
                }
              } else {
                if (
                  batchScore.scoreBatchId === plateGroupedInsp.imageBatchId &&
                  isFilterHit(batchScore, scoreDefn, true) &&
                  batchScore.wellDropScoreGroupId == scoreGroup &&
                  batchScore.isAutoScore === false
                ) {
                  if (
                    !anyHitWells.some(
                      (well) =>
                        well.wellNumber === batchScore.wellNumber &&
                        well.plateId === plate.plateId,
                    )
                  ) {
                    const wellObject = {} as any;
                    wellObject.wellNumber = batchScore.wellNumber;
                    wellObject.plateId = plate.plateId;
                    wellObject.hit = true;
                    anyHitWells.push(wellObject);
                  }
                }
              }
            }
          }
        });
      });
    }

    dispatch(setExpHitWell(anyHitWells));
  };

  const handleAllHits = async () => {
    const allHitWells: any[] = [];
    const selectedInspection = expData.inspections.find(
      (insp) => insp.id === selectedDropImageParameters.inspection.id,
    );
    const dropNumber = _dropNumber;

    if (scoreGroupType === 'All Scores') {
      for (const plate of expData.plateDto) {
        const plateScores = plate.plateScores;
        const plateGroupedInsp = selectedInspection.groupedInspections.find(
          (grp) => grp.plateId === plate.plateId,
        );

        if (!plateGroupedInsp || !plateScores) {
          continue;
        }

        const allBatchScores: any[] = plateScores.flat();

        const scoresByWell = new Map<number, any[]>();
        for (const batchScore of allBatchScores) {
          if (
            batchScore.dropNumber === dropNumber &&
            batchScore.scoreBatchId === plateGroupedInsp.imageBatchId
          ) {
            if (!scoresByWell.has(batchScore.wellNumber)) {
              scoresByWell.set(batchScore.wellNumber, []);
            }
            scoresByWell.get(batchScore.wellNumber)!.push(batchScore);
          }
        }

        for (const [wellNumber, scores] of scoresByWell.entries()) {
          let totalCount = 0;
          let hitCount = 0;

          for (const batchScore of scores) {
            const scoreDefn = GetScoreDefn().find(
              (s) => s.wellDropScoreId === batchScore.wellDropScoreId,
            );
            if (!scoreDefn && !isUvPositive(batchScore)) {
              continue;
            }

            if (scoreGroup == 0) {
              totalCount++;
              if (isHitScore(batchScore, scoreDefn)) {
                hitCount++;
              }
            } else {
              if (batchScore.wellDropScoreGroupId == scoreGroup) {
                totalCount++;
                if (isHitScore(batchScore, scoreDefn)) {
                  hitCount++;
                }
              }
            }
          }

          if (
            totalCount > 0 &&
            totalCount === hitCount &&
            (!uvHitsOnly || scores.some(isUvPositive))
          ) {
            const wellObject = {} as any;
            wellObject.wellNumber = wellNumber;
            wellObject.plateId = plate.plateId;
            wellObject.hit = true;
            allHitWells.push(wellObject);
          }
        }
      }
    } else if (scoreGroupType === 'Auto Scores') {
      if (scoreGroup == 0) {
        for (const plate of expData.plateDto) {
          const plateGroupedInsp = selectedInspection.groupedInspections.find(
            (grp) => grp.plateId === plate.plateId,
          );
          let currentWellNumber = 0;
          let wellScoreCount = 0;
          let hitScoreCount = 0;
          let wellHasUvPositive = false;

          const plateScores = plate.plateScores;

          plateScores.forEach((pltScore) => {
            pltScore.forEach((batchScore) => {
              const scoreDefn = GetScoreDefn().find(
                (s) => s.wellDropScoreId === batchScore.wellDropScoreId,
              );
              if (!scoreDefn && !isUvPositive(batchScore)) {
                return;
              }

              if (
                batchScore.dropNumber === dropNumber &&
                batchScore.scoreBatchId === plateGroupedInsp.imageBatchId
              ) {
                if (currentWellNumber !== batchScore.wellNumber) {
                  currentWellNumber = batchScore.wellNumber;
                  wellScoreCount = 0;
                  hitScoreCount = 0;
                  wellHasUvPositive = false;
                }

                if (isUvPositive(batchScore)) {
                  wellHasUvPositive = true;
                }

                if (
                  currentWellNumber === batchScore.wellNumber &&
                  batchScore.isAutoScore == true
                ) {
                  wellScoreCount++;
                  if (isHitScore(batchScore, scoreDefn)) {
                    hitScoreCount++;
                  }
                }
              }
            });
            if (
              wellScoreCount > 0 &&
              wellScoreCount === hitScoreCount &&
              (!uvHitsOnly || wellHasUvPositive)
            ) {
              const wellObject = {} as any;
              wellObject.wellNumber = currentWellNumber;
              wellObject.plateId = plate.plateId;
              wellObject.hit = true;
              allHitWells.push(wellObject);

              wellScoreCount = 0;
              hitScoreCount = 0;
            } else {
              wellScoreCount = 0;
              hitScoreCount = 0;
            }
          });
        }
      } else {
        for (const plate of expData.plateDto) {
          const plateGroupedInsp = selectedInspection.groupedInspections.find(
            (grp) => grp.plateId === plate.plateId,
          );
          let currentWellNumber = 0;
          let hitScoreCount = 0;
          let wellScoreGroupCount = 0;
          let wellHasUvPositive = false;

          const plateScores = plate.plateScores;

          plateScores.forEach((pltScore) => {
            pltScore.forEach((batchScore) => {
              const scoreDefn = GetScoreDefn().find(
                (s) => s.wellDropScoreId === batchScore.wellDropScoreId,
              );
              if (!scoreDefn && !isUvPositive(batchScore)) {
                return;
              }

              if (
                batchScore.dropNumber === dropNumber &&
                batchScore.scoreBatchId === plateGroupedInsp.imageBatchId
              ) {
                if (currentWellNumber !== batchScore.wellNumber) {
                  currentWellNumber = batchScore.wellNumber;
                  wellScoreGroupCount = 0;
                  hitScoreCount = 0;
                  wellHasUvPositive = false;
                }

                if (isUvPositive(batchScore)) {
                  wellHasUvPositive = true;
                }

                if (
                  currentWellNumber === batchScore.wellNumber &&
                  batchScore.isAutoScore === true &&
                  batchScore.wellDropScoreGroupId == scoreGroup
                ) {
                  wellScoreGroupCount++;
                  if (isHitScore(batchScore, scoreDefn)) {
                    hitScoreCount++;
                  }
                }
              }
            });
            if (
              wellScoreGroupCount > 0 &&
              wellScoreGroupCount === hitScoreCount &&
              (!uvHitsOnly || wellHasUvPositive)
            ) {
              const wellObject = {} as any;
              wellObject.wellNumber = currentWellNumber;
              wellObject.plateId = plate.plateId;
              wellObject.hit = true;
              allHitWells.push(wellObject);
              hitScoreCount = 0;
            } else {
              hitScoreCount = 0;
              wellScoreGroupCount = 0;
            }
          });
        }
      }
    } else if (scoreGroupType === 'Manual Scores') {
      if (scoreGroup == 0) {
        for (const plate of expData.plateDto) {
          const plateGroupedInsp = selectedInspection.groupedInspections.find(
            (grp) => grp.plateId === plate.plateId,
          );

          let currentWellNumber = 0;
          let wellScoreCount = 0;
          let hitScoreCount = 0;
          let wellHasUvPositive = false;

          const plateScores = plate.plateScores;

          plateScores.forEach((pltScore) => {
            pltScore.forEach((batchScore) => {
              const scoreDefn = GetScoreDefn().find(
                (s) => s.wellDropScoreId === batchScore.wellDropScoreId,
              );
              if (!scoreDefn && !isUvPositive(batchScore)) {
                return;
              }

              if (
                batchScore.dropNumber === dropNumber &&
                batchScore.scoreBatchId === plateGroupedInsp.imageBatchId
              ) {
                if (currentWellNumber !== batchScore.wellNumber) {
                  currentWellNumber = batchScore.wellNumber;
                  wellScoreCount = 0;
                  hitScoreCount = 0;
                  wellHasUvPositive = false;
                }

                if (isUvPositive(batchScore)) {
                  wellHasUvPositive = true;
                }

                if (batchScore.isAutoScore === false) {
                  if (
                    currentWellNumber === batchScore.wellNumber &&
                    isHitScore(batchScore, scoreDefn, true)
                  ) {
                    wellScoreCount++;
                    hitScoreCount++;
                  } else if (currentWellNumber === batchScore.wellNumber) {
                    wellScoreCount++;
                  }
                }
              }
            });
            if (
              hitScoreCount > 0 &&
              wellScoreCount === hitScoreCount &&
              (!uvHitsOnly || wellHasUvPositive)
            ) {
              const wellObject = {} as any;
              wellObject.wellNumber = currentWellNumber;
              wellObject.plateId = plate.plateId;
              wellObject.hit = true;
              allHitWells.push(wellObject);

              wellScoreCount = 0;
              hitScoreCount = 0;
            } else {
              wellScoreCount = 0;
              hitScoreCount = 0;
            }
          });
        }
      } else {
        for (const plate of expData.plateDto) {
          const plateGroupedInsp = selectedInspection.groupedInspections.find(
            (grp) => grp.plateId === plate.plateId,
          );

          let currentWellNumber = 0;
          let hitScoreCount = 0;
          let wellScoreGroupCount = 0;
          let wellHasUvPositive = false;
          const plateScores = plate.plateScores;

          plateScores.forEach((pltScore) => {
            pltScore.forEach((batchScore) => {
              const scoreDefn = GetScoreDefn().find(
                (s) => s.wellDropScoreId === batchScore.wellDropScoreId,
              );
              if (!scoreDefn && !isUvPositive(batchScore)) {
                return;
              }

              if (
                batchScore.dropNumber === dropNumber &&
                batchScore.scoreBatchId == plateGroupedInsp.imageBatchId
              ) {
                if (currentWellNumber !== batchScore.wellNumber) {
                  currentWellNumber = batchScore.wellNumber;
                  wellScoreGroupCount = 0;
                  hitScoreCount = 0;
                  wellHasUvPositive = false;
                }

                if (isUvPositive(batchScore)) {
                  wellHasUvPositive = true;
                }

                if (
                  currentWellNumber === batchScore.wellNumber &&
                  isHitScore(batchScore, scoreDefn, true) &&
                  batchScore.isAutoScore === false &&
                  batchScore.wellDropScoreGroupId == scoreGroup
                ) {
                  hitScoreCount++;
                  wellScoreGroupCount++;
                } else {
                  if (batchScore.wellDropScoreGroupId == scoreGroup) {
                    wellScoreGroupCount++;
                  }
                }
              }
            });
            if (
              wellScoreGroupCount > 0 &&
              wellScoreGroupCount === hitScoreCount &&
              (!uvHitsOnly || wellHasUvPositive)
            ) {
              const wellObject = {} as any;
              wellObject.wellNumber = currentWellNumber;
              wellObject.plateId = plate.plateId;
              wellObject.hit = true;
              allHitWells.push(wellObject);

              hitScoreCount = 0;
            } else {
              hitScoreCount = 0;
              wellScoreGroupCount = 0;
            }
          });
        }
      }
    }

    dispatch(setExpHitWell(allHitWells));
  };

  const refreshHitFilter = (activeHitButton = hitButtonName) => {
    if (uvHitsOnly) {
      if (activeHitButton === ScoreConstants.allHit) {
        handleAllHits();
      } else {
        handleAnyHit();
      }
    } else if (activeHitButton === ScoreConstants.anyHit) {
      handleAnyHit();
    } else if (activeHitButton === ScoreConstants.allHit) {
      handleAllHits();
    } else {
      dispatch(setExpHitWell(undefined));
    }
  };

  const scoreGroupTypeOptions = [
    {
      value: 1,
      name: 'All Scores',
    },
    {
      value: 2,
      name: 'Auto Scores',
    },
    {
      value: 3,
      name: 'Manual Scores',
    },
  ];

  const scoringToolsButtons = [
    {
      Icon: <MarkAsInteresting id="scoringtools-mark-icon" />,
      title: markIntresting ? 'Unmark as Interesting' : 'Mark as Interesting',
      type: '',
      showActive: false,
      onClick: () => markInterestingWellDrop('i', markIntresting, markIntresting),
      isDisabled: false,
    },
    {
      Icon: autoSelectNextWell ? (
        <OnIcon id="scoringtools-selectwell-icon" />
      ) : (
        <OffIcon />
      ),
      title: 'Auto Select Next Well',
      type: '',
      showActive: false,
      onClick: toggleAutoSelectNextWell,
      isDisabled: false,
    },
    {
      Icon: hideAutoScores ? (
        <OnIcon id="scoringtools-hideautoscores-icon" />
      ) : (
        <OffIcon />
      ),
      title: 'Hide Auto Scores',
      type: '',
      showActive: false,
      onClick: toggleHideAutoScores,
      isDisabled: false,
    },
  ];

  if (!wellDetails) {
    return;
  }
  return (
    <div
      id="scoringtools-wrapper"
      style={{
        width: '100%',
        height: '78vh',
        padding: '0px 10px',
      }}
    >
      <div
        id="scoringtools-container"
        className="w-100pr h-100 overflow-auto"
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div id="scoringtools-filterscore-row" className="scoring-tool-first-row">
          <div
            id="scoringtools-filterscore-label"
            className="filter-score-text center-align-both-axis"
          >
            Filter Score
          </div>
          <ScoresDropDown
            dropdownHeight={'80%'}
            dropdownwidth={'70%'}
            defaultText="Default Scores"
            optionsList={scoresOptionsList}
          ></ScoresDropDown>
        </div>
        {!rmLite ? (
          <>
            <div id="scoringtools-scoregrouptype-row" className="scoring-tool-first-row">
              <div
                id="scoringtools-scoregrouptype-label"
                className="filter-score-text center-align-both-axis"
              >
                Score Group Type
              </div>
              <CustomSelect
                value={scoreGroupType}
                onChange={handleScoreGroupType}
                options={scoreGroupTypeOptions}
                containerStyle={{
                  width: '70%',
                  height: '80%',
                  display: 'felx',
                  justifyContent: 'space-between',
                }}
              />
            </div>

            <div id="scoringtools-scoregroup-row" className="scoring-tool-first-row">
              <div
                id="scoringtools-scoregroup-label"
                className="filter-score-text center-align-both-axis"
              >
                Score Group
              </div>

              <CustomSelect
                value={scoreGroup}
                onChange={handleScoreGroup}
                options={scoreGroupOptions}
                containerStyle={{
                  width: '70%',
                  height: '80%',
                  display: 'felx',
                  justifyContent: 'space-between',
                }}
              />
            </div>
          </>
        ) : null}
        {!isDropMode && !isSnfRoute && !rmLite && (
          <>
            <div id="scoringtools-hitbuttons-row" className="scoring-tool-first-row">
              <div
                id="scoringtools-hitbuttons-label"
                className="filter-score-text center-align-both-axis"
              >
                Filter Score
              </div>

              <Button
                id="scoringtools-hitbuttons-anyhit"
                className="scoring-hit-button"
                style={{
                  backgroundColor: hitButtonName === ScoreConstants.anyHit ? '#D3D3D3' : '#FFFFFF',
                  borderColor: '#BDBDBD',
                  color: '#000000',
                }}
                onClick={(event) => {
                  event.currentTarget.blur();
                  if (hitButtonName === ScoreConstants.anyHit) {
                    dispatch(setHitButtonName(''));
                    refreshHitFilter('');
                  } else {
                    dispatch(setHitButtonName(ScoreConstants.anyHit));
                    refreshHitFilter(ScoreConstants.anyHit);
                  }
                }}
              >
                <img
                  id="scoringtools-hitbutton-anyhit-icon"
                  className="hits-icons"
                  src={AnyHitIcon}
                />
                Any Hit
              </Button>

              <Button
                id="scoringtools-hitbutton-allhit"
                className="scoring-hit-button"
                style={{
                  backgroundColor: hitButtonName === ScoreConstants.allHit ? '#D3D3D3' : '#FFFFFF',
                  borderColor: '#BDBDBD',
                  color: '#000000',
                }}
                onClick={(event) => {
                  event.currentTarget.blur();
                  if (hitButtonName === ScoreConstants.allHit) {
                    dispatch(setHitButtonName(''));
                    refreshHitFilter('');
                  } else {
                    dispatch(setHitButtonName(ScoreConstants.allHit));
                    refreshHitFilter(ScoreConstants.allHit);
                  }
                }}
              >
                <img
                  id="scoringtools-hitbutton-allhit-icon"
                  className="hits-icons"
                  src={AllHitsIcon}
                />
                All Hits
              </Button>
            </div>
            <div
              id="scoringtools-uv-hits-only-row"
              className="scoring-tool-first-row pl-[30.6%]"
            >
              <FmlxCheckBox
                checked={uvHitsOnly}
                onChange={(e) => {
                  dispatch(setUvHitsOnly(e.target.checked));
                }}
                label="UV Hits only"
              />
            </div>
          </>
        )}
        {isSnfRoute && <HitsButtons />}
        <div
          id="scoringtools-hitsbuttons-snf"
          className="scoring-tool-second-row pl-[30.6%]"
        >
          <ExperimentSidebarButtons
            height={'100%'}
            width={'100%'}
            buttons={scoringToolsButtons}
          ></ExperimentSidebarButtons>
        </div>
      </div>
    </div>
  );
}

export default React.memo(ScoringTools);
